import { useCallback, useContext, useMemo } from 'react';
import { ConversationItem } from '@zg-rentals/satellite-components';
import ConditionalWrapper from '../../ConditionalWrapper';
import ConversationItemFooter from './ConversationItemFooter';
import SwipeableWrapper from './SwipeableWrapper';
import { CONVERSATION_ITEM_SELECT_EVENT_TYPE_ID } from '../../../constants/clickstreamEvents/conversationItem';
import { AppContext } from '../../../contexts/AppContextProvider';
import { InboxPageContext } from '../../../contexts/InboxPageContextProvider';
import conversationUtils from '../../../utils/conversationUtils';
import type { ConversationListConversation, ConversationSelectArgs } from '../index';
import useDevices from '../../../hooks/useDevices';

type ConversationListItemProps = {
  conversation: ConversationListConversation;
  onSelect: (args: ConversationSelectArgs) => void;
};

export default function ConversationItemContainer({ conversation, onSelect }: ConversationListItemProps) {
  const { isImpersonator } = useContext(AppContext);
  const { conversationId: conversationIdFromUrl } = useContext(InboxPageContext);
  const { isDesktopView, isMobileView } = useDevices();

  const {
    activeParticipantsInConversation,
    address,
    conversation: messages,
    conversationId,
    hasUnreadMessage,
    inquiry,
    isConversationBlockedByZillow,
    listingAlias,
    referenceName,
    status,
    statusLabel,
  } = conversation;

  const { isPhoneLead } = inquiry || {};
  const { isArchived, isFavorite, isSpam } = status;

  const latestMessage = useMemo(() => {
    const filtered = messages.filter(({ messagePreview }) => messagePreview);
    return filtered[filtered.length - 1];
  }, [messages]);

  const { isMessageOwner, messagePreview, messageDateMs } = latestMessage || {};

  const streetAddress = address?.split(',')[0] || '';
  const isStarConversationIconDisabled =
    isImpersonator || isSpam || isArchived || (!isFavorite && isConversationBlockedByZillow);
  const isSelected = conversationIdFromUrl === conversationId && isDesktopView;

  const participantDisplay = useMemo(() => {
    if (isPhoneLead) {
      return conversationUtils.getPhoneLeadParticipantDisplay({
        name: inquiry?.name || '',
        phoneNumber: inquiry?.phone || '',
      });
    }
    return conversationUtils.getParticipantsNameWithCount({
      activeParticipantsInConversation,
      referenceName: referenceName!,
    });
  }, [isPhoneLead, inquiry?.name, inquiry?.phone, activeParticipantsInConversation, referenceName]);

  const handleClick = useCallback(() => {
    onSelect({
      selectedConversationId: conversationId,
      selectedListingAlias: listingAlias,
    });
  }, [onSelect, conversationId, listingAlias]);

  return (
    <ConditionalWrapper
      shouldRenderWrapperElement={isMobileView}
      wrapper={(children) => <SwipeableWrapper conversationId={conversationId}>{children}</SwipeableWrapper>}
    >
      <ConversationItem
        isArchived={isArchived}
        isMessageOwner={isMessageOwner ?? false}
        isPhoneLead={isPhoneLead ?? false}
        isSelected={isSelected}
        isSpamOrReported={isSpam}
        isUnread={hasUnreadMessage}
        latestMessage={messagePreview ?? ''}
        latestMessageDateMs={messageDateMs ?? ''}
        subtitle={streetAddress}
        title={participantDisplay}
        onClick={handleClick}
        dataEventId={CONVERSATION_ITEM_SELECT_EVENT_TYPE_ID}
        footer={
          <ConversationItemFooter
            conversationId={conversationId}
            isFavorite={isFavorite}
            statusLabel={statusLabel}
            isStarConversationIconDisabled={isStarConversationIconDisabled}
          />
        }
      />
    </ConditionalWrapper>
  );
}
